import Image from 'components/shared/image/image';
import useIsMobile from 'hooks/utils/useIsMobile';
import useCollectionSelectItemTracking from 'hooks/tracking/useCollectionSelectItemTracking';
import { useEffect, useState } from 'react';
import getShopifyData from 'lib/shopify/getShopifyData';
import { useSettings } from 'hooks';
import cn from 'classnames';
import SiteLink from 'components/shared/sitelink/sitelink';
import { addLocaleToHref } from 'lib/utils/helpers';
import ArrowRight from 'components/shared/arrow-right';
import { useRouter } from 'next/router';
import CardItem from '../highlights/cardItem/cardItem';
import styles from './productGrid.module.scss';

const ProductGrid = ({ data, locale }) => {
  const CAMPAIGN_STIEGLITZ = 'Campaign - Stieglitz';
  const { products, imagesInBetween, resources, title, link } = data;
  const isMobile = useIsMobile();
  const { setSaveItemSelectionList } = useSettings();
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [allGridItems, setAllGridItems] = useState([]);
  const router = useRouter();

  useEffect(() => {
    const updateProductData = (product, shopifyData) => {
      const updatedShopifyData = shopifyData?.find(
        ({ productSKU }) => productSKU === product?.shopifyData?.variants?.[0]?.sku
      );
      if (product?.shopifyData) {
        // eslint-disable-next-line no-param-reassign
        product.shopifyData.variants[0] = {
          ...product.shopifyData.variants[0],
          ...(updatedShopifyData?.productCompareAtPrice && {
            compareAtPrice: updatedShopifyData?.productCompareAtPrice,
          }),
          outOfStock: updatedShopifyData?.outOfStock,
          ...(updatedShopifyData?.productPrice && { price: updatedShopifyData?.productPrice }),
          ...(updatedShopifyData?.productTitle && { title: updatedShopifyData?.productTitle }),
        };
      }
    };

    const filterOutOfStock = arr => {
      const filteredProducts = arr.filter(item => {
        if (item.type === 'PageProduct') {
          return !item.shopifyData?.variants[0]?.outOfStock;
        }
        if (item.type === 'PageProductBundle' && item.products) {
          return item.products.filter(prod => !prod.shopifyData?.variants[0]?.outOfStock).length;
        }
        return true;
      });

      return filteredProducts;
    };

    const regroupShopifyProducts = shopifyData => {
      if (shopifyData?.length > 0) {
        // updating prices directly in filtered products which are stored in context
        products.forEach(prod => {
          if (prod.products) {
            const bundle = [];
            prod.products.forEach(item => {
              if (item.shopifyData) {
                bundle.push(
                  shopifyData.find(
                    ({ productSKU }) => item.shopifyData?.variants?.[0]?.sku === productSKU
                  )
                );
              }
            });
            // eslint-disable-next-line no-param-reassign
            prod.cardShopifyData = bundle;
          } else if (prod.shopifyData) {
            // eslint-disable-next-line no-param-reassign
            prod.cardShopifyData = shopifyData.find(
              ({ productSKU }) => prod.shopifyData.variants[0]?.sku === productSKU
            );
          }
        });
      }

      setAllGridItems(filterOutOfStock(products));
    };

    const shopifyDataRetrieval = async () => {
      const productIds = [];

      products.forEach(prod => {
        if (prod.type === 'PageProduct') {
          productIds.push(`gid://shopify/Product/${prod.shopifyData?.variants[0].product_id}`);
        }

        prod.products?.forEach(item =>
          productIds.push(`gid://shopify/Product/${item.shopifyData?.variants[0].product_id}`)
        );
      });

      if (productIds.length) {
        const pdpShopifyData = await getShopifyData({ productIds, locale });
        if (pdpShopifyData.length > 0) {
          products.forEach(p => {
            if (p.type === 'PageProductBundle') {
              p.products.forEach(bundleProd => updateProductData(bundleProd, pdpShopifyData));
            } else if (p.type === 'PageProduct') {
              updateProductData(p, pdpShopifyData);
            }
          });
        }

        regroupShopifyProducts(pdpShopifyData);
      } else {
        setAllGridItems(products);
      }
    };

    shopifyDataRetrieval();
  }, [products]);

  const decideInBetweenImage = index => {
    if (index === 2) return imagesInBetween[0].image;
    if (index === 8) return imagesInBetween[1].image;
    return null;
  };
  const trackSelectItem = (product, index) => {
    if (product.type === 'PageProduct' && shopifyProducts?.[index]) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useCollectionSelectItemTracking({
        product: {
          name: shopifyProducts?.[index]?.productTitle,
          id: product.shopifyData?.variants?.[0]?.product_id,
          category: shopifyProducts?.[index]?.productVendor,
          price: shopifyProducts?.[index]?.productPrice,
          position: index,
        },
        list: `Category: ${title || 'Product Grid'}`,
        setSaveItemSelectionList,
        locale,
      });
    }

    if (product.type === 'PageProductBundle') {
      shopifyProducts?.[index]?.forEach((bundleProduct, i) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useCollectionSelectItemTracking({
          product: {
            name: bundleProduct?.productTitle,
            id: product.products[i].shopifyData?.variants?.[0]?.product_id,
            category: bundleProduct?.productVendor,
            price: bundleProduct?.productPrice,
            position: index,
          },
          list: `Category: ${title || 'Product Grid'}`,
          setSaveItemSelectionList,
          locale,
        });
      });
    }
  };

  return (
    <section id="sectionProductGrid" className={styles.container}>
      <div className={styles.header}>
        {title && <h2 className={styles.title}>{title}</h2>}
        {link && (
          <SiteLink
            locale={locale}
            className={styles.linkToMore}
            href={addLocaleToHref(link.link.slug, locale, router)}
          >
            {isMobile ? <ArrowRight /> : link.text}
          </SiteLink>
        )}
      </div>

      <div
        id="sectionProductGrid"
        className={cn(
          data.name === CAMPAIGN_STIEGLITZ ? styles.stieglitzProducts : styles.products
        )}
      >
        {allGridItems.map((product, index) => {
          if (!isMobile && (index === 2 || index === 8) && imagesInBetween.length > 0)
            return (
              <Image
                className={styles.gridItem}
                key={product?.id}
                src={decideInBetweenImage(index)}
              />
            );
          return (
            <CardItem
              onClick={() => trackSelectItem(product, index)}
              locale={locale}
              className={styles.gridItem}
              key={product?.id}
              itemData={product}
              globalModules={resources}
              shopifyProduct={product.cardShopifyData}
              itemIndex={index}
              pageTitle={title || 'Product Grid'}
            />
          );
        })}
      </div>
    </section>
  );
};

export default ProductGrid;
